import React, { useCallback, useRef, useState } from 'react';
import { NJText } from '@engie-group/fluid-design-system-react';

type UploadState = 'idle' | 'uploading' | 'success' | 'error';

export interface FileUploadCardProps {
  accept?: string;
  acceptLabel?: string;
  maxSizeMb?: number;
  onFileAccepted?: (file: File) => void;
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export default function FileUploadCard({
  accept = '.csv,.xlsx,.xls',
  acceptLabel = 'CSV, Excel (.xlsx, .xls)',
  maxSizeMb = 10,
  onFileAccepted,
}: FileUploadCardProps) {
  const [state, setState] = useState<UploadState>('idle');
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState(0);
  const [progress, setProgress] = useState(0);
  const [loadedBytes, setLoadedBytes] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startUpload = useCallback(
    (file: File) => {
      const acceptedExts = accept.split(',').map(e => e.trim().toLowerCase());
      const ext = '.' + (file.name.split('.').pop() ?? '').toLowerCase();

      if (!acceptedExts.includes(ext)) {
        setFileName(file.name);
        setErrorMsg(
          `The file "${file.name}" is not accepted. Accepted formats: ${acceptLabel}`,
        );
        setState('error');
        return;
      }

      if (file.size > maxSizeMb * 1024 * 1024) {
        setFileName(file.name);
        setErrorMsg(`Le fichier dépasse la taille maximale de ${maxSizeMb} Mo.`);
        setState('error');
        return;
      }

      setFileName(file.name);
      setFileSize(file.size);
      setProgress(0);
      setLoadedBytes(0);
      setErrorMsg(null);
      setState('uploading');

      if (timerRef.current) clearInterval(timerRef.current);
      let pct = 0;
      timerRef.current = setInterval(() => {
        pct += Math.floor(Math.random() * 18) + 8;
        if (pct >= 100) {
          pct = 100;
          if (timerRef.current) clearInterval(timerRef.current);
          setState('success');
          onFileAccepted?.(file);
        }
        setProgress(pct);
        setLoadedBytes(Math.round((file.size * pct) / 100));
      }, 250);
    },
    [accept, acceptLabel, maxSizeMb, onFileAccepted],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) startUpload(file);
      e.target.value = '';
    },
    [startUpload],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) startUpload(file);
    },
    [startUpload],
  );

  const handleReset = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    setState('idle');
    setFileName(null);
    setFileSize(0);
    setProgress(0);
    setErrorMsg(null);
  }, []);

  const isUploading = state === 'uploading';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {/* Drop zone */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => !isUploading && inputRef.current?.click()}
        onKeyDown={e => { if (!isUploading && (e.key === 'Enter' || e.key === ' ')) inputRef.current?.click(); }}
        onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        style={{
          boxSizing: 'border-box',
          border: `1px dashed ${isDragOver ? 'var(--nj-semantic-color-border-brand-default)' : '#9EADB8'}`,
          borderRadius: 'var(--nj-core-border-radius-200)',
          background: isDragOver ? 'rgba(59,130,246,0.05)' : 'transparent',
          padding: '32px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '12px',
          height: '225px',
          alignSelf: 'stretch',
          flexGrow: 0,
          cursor: isUploading ? 'default' : 'pointer',
          userSelect: 'none',
          outline: 'none',
        }}
      >
        <span
          className="material-icons-outlined"
          style={{
            width: '48px',
            height: '48px',
            fontSize: '48px',
            flex: 'none',
            flexGrow: 0,
            color: isUploading
              ? 'var(--nj-semantic-color-text-brand-default)'
              : '#9EADB8',
          }}
        >
          cloud_upload
        </span>

        <span style={{
          display: 'flex',
          alignItems: 'center',
          fontFamily: "'Lato', sans-serif",
          fontStyle: 'normal',
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '20px',
          color: '#171D21',
          width: '192px',
          height: '20px',
          flex: 'none',
          flexGrow: 0,
          textAlign: 'center',
        }}>
          Glissez-déposez le fichier ici ou
        </span>

        {!isUploading && (
          <>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                padding: 0,
                gap: '4px',
                width: '92px',
                height: '20px',
                flex: 'none',
                flexGrow: 0,
              }}
            >
              <span
                style={{
                  color: 'var(--nj-semantic-color-brand-primary, #0078D4)',
                  fontFamily: "'Lato', sans-serif",
                  fontSize: '14px',
                  fontWeight: 500,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  lineHeight: '20px',
                  whiteSpace: 'nowrap',
                }}
              >
                Click to upload
              </span>
            </div>
            <NJText
              scale="xs"
              style={{
                margin: 0,
                color: 'var(--nj-semantic-color-text-neutral-tertiary-default, #60798B)',
                textAlign: 'center',
                fontFamily: "'Lato', sans-serif",
              }}
            >
              Formats acceptés : {acceptLabel} • Taille maximale : {maxSizeMb} Mo
            </NJText>
          </>
        )}
      </div>

      {/* Uploading */}
      {state === 'uploading' && fileName && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <NJText scale="md" style={{ margin: 0, fontWeight: 600, fontFamily: "'Lato', sans-serif" }}>
            {fileName}
          </NJText>
          <NJText scale="sm" style={{ margin: 0, color: 'var(--nj-semantic-color-text-secondary-default)', fontFamily: "'Lato', sans-serif" }}>
            Upload en cours... {formatSize(loadedBytes)} / {formatSize(fileSize)}
          </NJText>
          <div style={{
            height: '8px',
            borderRadius: '4px',
            background: 'var(--nj-semantic-color-background-tertiary-default, #E8ECEF)',
            overflow: 'hidden',
            marginTop: '4px',
            flex: 'none',
            order: 1,
            alignSelf: 'stretch',
            flexGrow: 0,
          }}>
            <div style={{
              width: `${progress}%`, height: '100%',
              background: 'var(--nj-semantic-color-text-brand-default, #0078D4)',
              borderRadius: '4px', transition: 'width 0.2s ease',
            }} />
          </div>
          <NJText scale="sm" style={{ margin: 0, color: 'var(--nj-semantic-color-text-secondary-default)', fontFamily: "'Lato', sans-serif" }}>
            {progress}%
          </NJText>
        </div>
      )}

      {/* Success */}
      {state === 'success' && fileName && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px',
          padding: '12px 16px',
          background: '#F0FBF0',
          border: '1px solid #4CAF50',
          borderRadius: 'var(--nj-core-border-radius-200)',
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '22px', height: '22px', borderRadius: '50%',
            background: '#2E7D32', flexShrink: 0,
          }}>
            <span className="material-icons" style={{ fontSize: '16px', color: '#fff' }}>check</span>
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <NJText scale="sm" style={{ margin: '0 0 2px 0', fontWeight: 700, fontFamily: "'Lato', sans-serif" }}>
              {fileName}
            </NJText>
            <NJText scale="sm" style={{ margin: 0, color: 'var(--nj-semantic-color-text-secondary-default)', fontFamily: "'Lato', sans-serif" }}>
              {formatSize(fileSize)} • Uploadé avec succès
            </NJText>
          </div>
          <button
            type="button"
            onClick={e => { e.stopPropagation(); handleReset(); }}
            aria-label="Supprimer le fichier"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
          >
            <span className="material-icons" style={{ fontSize: '20px', color: 'var(--nj-semantic-color-text-secondary-default)' }}>delete</span>
          </button>
        </div>
      )}

      {/* Error */}
      {state === 'error' && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '12px',
          padding: '12px 16px',
          background: '#FFEAE5',
          border: '1px solid #F34E46',
          borderRadius: 'var(--nj-core-border-radius-200)',
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '22px', height: '22px', borderRadius: '50%',
            background: '#C62828', flexShrink: 0, marginTop: '2px',
          }}>
            <span className="material-icons" style={{ fontSize: '16px', color: '#fff' }}>close</span>
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <NJText scale="sm" style={{ margin: '0 0 4px 0', fontWeight: 700, fontFamily: "'Lato', sans-serif" }}>
              Unsupported file format
            </NJText>
            <NJText scale="sm" style={{ margin: 0, color: 'var(--nj-semantic-color-text-secondary-default)', fontFamily: "'Lato', sans-serif" }}>
              {errorMsg}
            </NJText>
          </div>
          <button
            type="button"
            onClick={e => { e.stopPropagation(); handleReset(); }}
            aria-label="Fermer"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', flexShrink: 0 }}
          >
            <span className="material-icons" style={{ fontSize: '20px', color: 'var(--nj-semantic-color-text-secondary-default)' }}>close</span>
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={handleInputChange}
      />
    </div>
  );
}
